import { REQUEST_STATUS } from "@/ts/enums/RequestStatus";
import { createTransform } from "redux-persist";

import type { UserElectivesState } from "./slices/userElectivesSlice";

/**
 * Prevents request status fields from being persisted in sessionStorage.
 *
 * Without this, if the page is refreshed mid-flight (while a request is
 * PENDING), redux-persist rehydrates the store with PENDING status and no
 * subsequent action ever moves it to SUCCESS/ERROR — causing the page to
 * remain in an infinite loading state (BLD2-274 Bug 2).
 *
 * All request status fields are forced back to IDLE on rehydration so that
 * the component always triggers a fresh fetch on mount.
 */

const STATUS_FIELDS: (keyof UserElectivesState)[] = [
    "getElectiveListRequestStatus",
    "getEnrolledStudentElectivesRequestStatus",
    "getNotEnrolledStudentElectivesRequestStatus",
    "getValidateStudentStateRequirementRequestStatus",
    "saveDraftStudentElectivesRequestStatus",
    "enrolStudentForElectivesRequestStatus",
    "deleteStudentElectiveRequestStatus",
    "getElectiveDetailsRequestStatus"
];

export const userElectivesTransform = createTransform<
    UserElectivesState,
    Partial<UserElectivesState>
>(
    // Outbound (state → sessionStorage): persist everything as-is
    inboundState => inboundState,

    // Inbound (sessionStorage → state): reset all status fields to IDLE
    outboundState => {
        const resetStatuses = STATUS_FIELDS.reduce<
            Partial<UserElectivesState>
        >((acc, field) => {
            acc[field] = REQUEST_STATUS.IDLE as never;
            return acc;
        }, {});

        return {
            ...outboundState,
            ...resetStatuses
        };
    },
    { whitelist: ["userElectives"] }
);
